import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { _className, Button, ComponentSync, EditableDBItemV3, LL_H_C, LL_V_L, ModuleFE_Toaster, scrollToTop, } from '@nu-art/thunderstorm-frontend/index';
import { _keys, _values, dbObjectToId, MUSTNeverHappenException, reduceToMap, removeDBObjectKeys, sortArray } from '@nu-art/ts-common';
import { Header_Pathway } from '../Page_PathwayEditor/headers/Header_Pathway.js';
import { Header_ResolutionFunction } from './headers/Header_ResolutionFunctionEditor.js';
import './Component_ResolutionFunctionEditor.scss';
import { dispatch_showResolutionFunctionEditorPanel } from './dispatchers.js';
import { ModuleFE_Pathway, ModuleFE_PathwayState, ModuleFE_PathwayTransition } from '../../_entity.js';
import { Component_ResolutionFunctionCard } from './components/Component_ResolutionFunctionCard.js';
import { getEmptyResolutionFunction } from './const.js';
import { ModuleFE_Expression } from '@app/hcs-frontend';
import { dispatch_FocusResolutionFunction } from '../Page_PathwayEditor/dispatchers.js';
import { ModuleFE_PathwayEditor } from '../../modules/index.js';
import { Dialog_Confirmation } from '@app/styles';
export class Component_ResolutionFunctionEditor extends ComponentSync {
    termsListRef = React.createRef();
    headerMenuActions = [
        {
            label: 'HCS Variables', action: () => {
                const resolutionFunctions = Object.values(this.state.mapTransitionIdToEditableExpression).map(editable => editable.item);
                dispatch_showResolutionFunctionEditorPanel.dispatchUI({ resolutionFunctions: resolutionFunctions });
            }
        },
    ];
    // ######################## Life Cycle ########################
    __onExpressionUpdated(...params) {
        const selectedTransition = ModuleFE_PathwayTransition.cache.unique(this.props.selectedTransitionId);
        if (!selectedTransition || !selectedTransition.resolutionFunctionId)
            return;
        switch (params[0]) {
            case 'delete':
            case 'delete-multi':
                this.updateUIState();
                break;
            case 'update':
            case 'upsert-all':
            case 'create':
                this.updateUIState();
        }
    }
    __onPathwayTransitionUpdated(...params) {
        switch (params[0]) {
            case 'delete':
            case 'delete-multi': {
                this.updateUIState();
                break;
            }
            case 'update':
            case 'upsert-all':
            case 'create':
                this.updateUIState();
        }
    }
    updateUIState() {
        // get the up to date transition ids
        const transitionIds = ModuleFE_PathwayTransition.cache.filter(t => {
            return t.sourceId === this.state.sourceStateId && t.pathwayIds.includes(ModuleFE_PathwayEditor.getViewedPathwayId());
        }).map(dbObjectToId);
        const relevantTransitionIds = sortArray(transitionIds, item => {
            const transitionDoc = ModuleFE_PathwayTransition.cache.unique(item);
            if (!transitionDoc)
                throw new MUSTNeverHappenException(`transition with id ${item} not found`);
            return transitionDoc.priorityIndex;
        });
        this.setState({
            mapTransitionIdToEditableExpression: this.getEditableItemsMapper(relevantTransitionIds),
            transitionIds: relevantTransitionIds
        });
    }
    addListItem = async (_resolutionFunction) => {
        const availableTransitionsIds = this.getAvailableTransitionIds();
        if (_resolutionFunction?._id)
            _resolutionFunction = removeDBObjectKeys(_resolutionFunction);
        if (!availableTransitionsIds.length)
            return ModuleFE_Toaster.toastError('Cannot add another term, all transitions are already in use');
        const transition = ModuleFE_PathwayTransition.cache.unique(availableTransitionsIds[0]);
        const resolutionFunction = this.createEditableItem(transition, _resolutionFunction);
        this.state.transitionIds.push(availableTransitionsIds[0]);
        this.state.mapTransitionIdToEditableExpression[availableTransitionsIds[0]] = resolutionFunction;
        this.forceUpdate();
    };
    async __onDuplicateTransitionTerm(term) {
        await this.addListItem(term);
    }
    deriveStateFromProps(nextProps, state) {
        state.mapTransitionIdToEditableExpression ??= this.getEditableItemsMapper(nextProps.pathwayTransitionIds);
        state.transitionIds ??= nextProps.pathwayTransitionIds;
        state.selectedTransitionId = nextProps.selectedTransitionId;
        state.sourceStateId ??= nextProps.sourceStateId;
        state.collapsedMap ??= reduceToMap(state.transitionIds, item => item, item => true);
        if (state.selectedTransitionId)
            state.focusEditor = true;
        return state;
    }
    getEditableItemsMapper(editingTransitionIds) {
        return reduceToMap(editingTransitionIds, id => id, transitionId => {
            const transition = ModuleFE_PathwayTransition.cache.unique(transitionId);
            const expression = ModuleFE_Expression.cache.unique(transition?.resolutionFunctionId);
            return this.createEditableItem(transition, expression);
        });
    }
    createEditableItem(transition, expression) {
        if (!transition || !transition._id)
            throw new MUSTNeverHappenException('can not create new resolution function without transition');
        let _transition = transition;
        const editableExpression = new EditableDBItemV3(expression || getEmptyResolutionFunction(_transition), ModuleFE_Expression);
        const onChange = async (editable) => {
            // @ts-ignore - must do this due to a conflicting change coming it from the props and the base component issue
            // with queueing re-renders
            this.state.mapTransitionIdToEditableExpression = {
                ...this.state.mapTransitionIdToEditableExpression,
                [_transition._id]: editable
            };
            this.forceUpdate(() => dispatch_FocusResolutionFunction.dispatchUI(_transition._id, editable, true));
        };
        const saveAction = async (item) => {
            const currentItem = this.state.mapTransitionIdToEditableExpression[_transition._id];
            const pathway = ModuleFE_Pathway.cache.unique(ModuleFE_PathwayEditor.getViewedPathwayId());
            if (!pathway)
                throw new MUSTNeverHappenException(`cannot find pathway with id ${ModuleFE_PathwayEditor.getViewedPathwayId()}`);
            // In order to save with errors we should catch the potential validation error and continue with the save
            //process. it's a bit of a hack so as saving with errors which is bad anyway
            try {
                currentItem.validate();
            }
            catch (err) {
                this.logError(err);
            }
            try {
                let _resolutionFunction = item;
                // check if it's being edited from a pathway child
                if (pathway._parentId && _transition._id && _transition.pathwayIds?.includes(pathway._parentId)) {
                    await new Promise(resolve => {
                        Dialog_Confirmation.show({
                            content: _jsx(_Fragment, { children: _jsx("div", { children: "By editing this resolution function, it'll detach it and its transition from the parent and it won't receive more updates" }) }),
                            buttons: [
                                {
                                    label: 'Yes, Edit',
                                    isAsync: true,
                                    action: async () => {
                                        const result = await ModuleFE_PathwayTransition._v1.detachTransitionContext({
                                            resolutionFunction: item,
                                            pathwayTransition: _transition,
                                            pathwayId: pathway._id
                                        }).executeSync();
                                        _resolutionFunction = result.resolutionFunctions.updated[0];
                                        const newTransition = result.pathwayTransitions.updated[0];
                                        this.props.functions.selectPathwayTransition(newTransition._id);
                                        _transition = newTransition;
                                        resolve();
                                    }
                                },
                                {
                                    label: 'Cancel',
                                    action: () => resolve(),
                                    isPreferred: true
                                }
                            ]
                        });
                    });
                }
                else {
                    const body = {
                        transitionId: _transition._id,
                        resolutionFunction: item
                    };
                    const { resolutionFunction } = await ModuleFE_PathwayTransition._v1.updateResolutionFunction(body).executeSync();
                    _resolutionFunction = resolutionFunction;
                }
                return _resolutionFunction;
            }
            catch (e) {
                return item;
            }
        };
        const deleteAction = async (item) => {
            // If needed delete from the db, otherwise only remove in the ui
            const resolutionFunctionId = item._id;
            if (resolutionFunctionId) {
                await ModuleFE_PathwayTransition._v1.deleteResolutionFunction({ transitionId: transition._id }).executeSync();
                ModuleFE_Toaster.toastSuccess('Term deleted successfully');
            }
            this.state.mapTransitionIdToEditableExpression[transition._id] = this.createEditableItem(transition);
            this.forceUpdate();
        };
        if (editableExpression.get('_id'))
            editableExpression.validate();
        editableExpression
            .setOnChanged(onChange)
            .setSaveAction(saveAction)
            .setOnDelete(deleteAction);
        return editableExpression;
    }
    onCollapse = (transitionId) => {
        this.state.collapsedMap[transitionId] = !this.state.collapsedMap[transitionId];
        this.forceUpdate();
    };
    collapseExpandAll = () => {
        const shouldCollapse = _values(this.state.collapsedMap).some(value => !value);
        const newCollapseMap = reduceToMap(_keys(this.state.collapsedMap), item => item, item => {
            return shouldCollapse;
        });
        this.setState({ collapsedMap: newCollapseMap });
    };
    componentDidMount() {
        const pathwayTransition = ModuleFE_PathwayTransition.cache.unique(this.props.pathwayTransitionIds[0]);
        if (!pathwayTransition)
            throw new MUSTNeverHappenException(`transition with id ${this.props.pathwayTransitionIds[0]} not found`);
        const currentPathwayId = ModuleFE_PathwayEditor.getViewedPathwayId();
        history.pushState({ page: 1 }, 'pathway-v2', `?id=${currentPathwayId}&type=pathway`);
        // select resolution function editable in case theres a selected transition when mounting
        if (this.state.selectedTransitionId) {
            const editable = this.state.mapTransitionIdToEditableExpression[this.state.selectedTransitionId];
            dispatch_FocusResolutionFunction.dispatchUI(this.state.selectedTransitionId, editable);
        }
        if (this.state.focusEditor)
            if (this.props.selectedTransitionId && this.termsListRef.current) {
                const termItem = document.querySelector(`[data-term-item="${this.props.selectedTransitionId}"]`);
                if (termItem)
                    scrollToTop(termItem, this.termsListRef, 'smooth');
            }
        window.addEventListener('popstate', this.handleBrowserNavigation);
    }
    componentWillUnmount() {
        window.removeEventListener('popstate', this.handleBrowserNavigation);
    }
    handleBrowserNavigation = async (e) => {
        e.preventDefault();
        if (this.props.selectedTransitionId) {
            this.props.functions.selectPathwayTransition(this.props.selectedTransitionId);
        }
        const pathwayTransition = ModuleFE_PathwayTransition.cache.unique(this.props.pathwayTransitionIds[0]);
        if (pathwayTransition && !this.props.selectedTransitionId) {
            this.props.functions.selectPathwayState(pathwayTransition.sourceId);
        }
        await this.props.functions.deselectResolutionFunctionEditor();
    };
    // ######################## Render ########################
    renderTopButtons = () => {
        return _jsx(LL_H_C, { className: 'pathway__top-buttons', children: _jsx(Button, { variant: 'secondary', className: 'pathway__top-buttons__back', onClick: async () => {
                    this.props.functions.deselectPathway();
                }, children: '< Back to list' }) });
    };
    renderHeader = () => {
        const pathwayTransition = ModuleFE_PathwayTransition.cache.unique(this.props.pathwayTransitionIds[0]);
        const pathwayState = ModuleFE_PathwayState.cache.unique(pathwayTransition?.sourceId);
        const viewedPathwayId = ModuleFE_PathwayEditor.getViewedPathwayId();
        if (!viewedPathwayId)
            throw new MUSTNeverHappenException('cannot render this without having focused pathway in context');
        const pathway = ModuleFE_Pathway.cache.unique(viewedPathwayId);
        return _jsxs(_Fragment, { children: [this.renderTopButtons(), _jsx(Header_Pathway, { pathway: new EditableDBItemV3(pathway, ModuleFE_Pathway), allowActions: false, functions: this.props.functions, clickableTitle: true, titleClickAction: async () => {
                        this.props.functions.deselectPathwayState();
                    }, isClickAsync: true }), _jsx(Header_ResolutionFunction, { expandCollapseAll: this.collapseExpandAll, expandStatus: _values(this.state.collapsedMap).some(value => !value), editables: _values(this.state.mapTransitionIdToEditableExpression), availableTransitionsIds: this.getAvailableTransitionIds(), deselectResolutionFunction: this.props.functions.deselectResolutionFunctionEditor, state: pathwayState, actions: this.headerMenuActions, allowActions: true })] });
    };
    renderListCard = (transitionId) => {
        const dbTransition = ModuleFE_PathwayTransition.cache.unique(transitionId);
        if (!dbTransition)
            throw new MUSTNeverHappenException(`cannot find transition with id ${transitionId}`);
        const resolutionClassName = _className(this.state.selectedTransitionId === transitionId && this.state.focusEditor && 'attention-grabber');
        const editable = this.state.mapTransitionIdToEditableExpression[transitionId];
        if (!editable) {
            return;
        }
        return _jsx(Component_ResolutionFunctionCard, { className: resolutionClassName, index: dbTransition.priorityIndex, collapsed: this.state.collapsedMap[transitionId], onCollapse: this.onCollapse, transitionId: transitionId, editable: editable }, `term-${transitionId}`);
    };
    renderBody = () => {
        return _jsx(LL_V_L, { className: 'transition-list', children: this.state.transitionIds.map(transitionId => this.renderListCard(transitionId)) });
    };
    getAvailableTransitionIds() {
        return this.props.pathwayTransitionIds.filter(transitionId => !this.state.transitionIds.includes(transitionId));
    }
    render() {
        return _jsxs(LL_V_L, { innerRef: this.termsListRef, className: 'match_parent resolution-function-editor', style: { padding: '30px', gap: '10px' }, children: [this.renderHeader(), this.renderBody()] });
    }
}
//# sourceMappingURL=Component_ResolutionFunctionEditor.js.map